// Inline "add bank" sub-form (SPEC §5.1).
//
// Unlike `AddBankAccountModal` which POSTs to /oms/users/<id>/bank-details/ immediately, this
// collects the four fields and emits them as `new_bank_details` into the picker's payload. The
// server persists atomically inside the raise/Authorize transaction, so a rolled-back request
// never leaves an orphaned bank row.
//
// Trade-off accepted: a form-fill lost to a 400 is not persisted. Operators retry the fields.

import { Form, Input, Select } from "antd";
import React, { useEffect } from "react";

import { BANK_CHOICES, IFSC_REGEX } from "../../returns/returns.api";
import type { NewBankDetails } from "./types";

interface Props {
  value: Partial<NewBankDetails> | null;
  onChange: (value: NewBankDetails | null) => void;
}

const EMPTY: NewBankDetails = {
  account_holder_name: "",
  account_number: "",
  ifsc: "",
  bank_name: "",
};

function isComplete(v: Partial<NewBankDetails>): v is NewBankDetails {
  return !!(
    v.account_holder_name?.trim() &&
    v.account_number?.trim() &&
    v.ifsc?.trim() &&
    IFSC_REGEX.test(v.ifsc.trim()) &&
    v.bank_name?.trim()
  );
}

export const AddBankInline: React.FC<Props> = ({ value, onChange }) => {
  const [form] = Form.useForm<NewBankDetails>();

  useEffect(() => {
    if (value) form.setFieldsValue({ ...EMPTY, ...value });
  }, [value, form]);

  const emit = () => {
    const current = { ...EMPTY, ...form.getFieldsValue() };
    if (current.ifsc) current.ifsc = current.ifsc.toUpperCase();
    onChange(isComplete(current) ? current : null);
  };

  return (
    <Form
      form={form}
      layout="vertical"
      size="small"
      style={{ paddingLeft: 24, marginTop: 8 }}
      onValuesChange={emit}
      initialValues={EMPTY}
    >
      <Form.Item name="bank_name" label="Bank"
                 rules={[{ required: true, message: "Pick the bank" }]}>
        <Select
          showSearch
          placeholder="Select a bank"
          optionFilterProp="label"
          options={BANK_CHOICES}
        />
      </Form.Item>
      <Form.Item name="account_holder_name" label="Account holder name"
                 rules={[{ required: true, max: 128 }]}>
        <Input placeholder="As on the bank record" />
      </Form.Item>
      <Form.Item name="account_number" label="Account number"
                 rules={[{ required: true, max: 32 }]}>
        <Input placeholder="e.g. 00001234567890" />
      </Form.Item>
      <Form.Item name="ifsc" label="IFSC"
                 rules={[
                   { required: true, message: "Enter the IFSC" },
                   { pattern: IFSC_REGEX, message: "IFSC must be 4 letters + 0 + 6 alphanumerics" },
                 ]}>
        <Input placeholder="e.g. HDFC0001234" style={{ textTransform: "uppercase" }} />
      </Form.Item>
    </Form>
  );
};
